import {useCrmFocus} from '../../hooks/useCrmSection';
import useInvoicePdfDownload from '../../hooks/useInvoicePdfDownload';
import RefillInvoiceSummary, { RefillRows } from '../../components/invoices/RefillInvoiceSummary';
import CreditNoteSummary from '../../components/invoices/CreditNoteSummary';
import PayPalInvoiceLink from '../../components/invoices/PayPalInvoiceLink';
import PayPalPaymentAudit from '../../components/invoices/PayPalPaymentAudit';
import { isCreditNote, isIssuedCreditNote, canCreateCreditNote, invoiceState } from '../../utils/invoiceDocument';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatNuf } from '../../utils/formatNuf';
import { formatCustomerName } from '../../utils/formatCustomerName';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  useMediaQuery,
  useTheme,
  Menu,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Speed as SpeedIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  LocationOn as LocationOnIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { invoicesApi, paymentsApi, advancesApi } from '../../services/api';
import { format, differenceInDays } from 'date-fns';
import SendEmailDialog from '../../components/invoices/SendEmailDialog';
import SendWhatsappDialog from '../../components/invoices/SendWhatsappDialog';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import MobileCardList from '../../components/MobileCardList';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatNumber = (value, decimals = 0) => {
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
};

const paymentNoteSx = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

// Componente riga campo
const FieldRow = ({ label, value, valueColor, bold = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 'bold' : 'normal', color: valueColor || 'inherit', fontSize: bold ? '0.9rem' : '0.8rem' }}>
      {value}
    </Typography>
  </Box>
);

// Campo inline compatto
const InlineField = ({ label, value, valueColor }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 2, mb: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{label}:</Typography>
    <Typography variant="body2" sx={{ fontWeight: 'medium', color: valueColor || 'inherit' }}>{value}</Typography>
  </Box>
);

const InvoiceDetail = () => {
  const { downloadPdf, pdfDownloading, pdfError, clearPdfError } = useInvoicePdfDownload();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [mobileActionsAnchor, setMobileActionsAnchor] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState(null);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [sendWhatsappDialogOpen, setSendWhatsappDialogOpen] = useState(false);
  
  // State per modifica pagamento/acconto
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date(),
    payment_method: 'cash',
    reference_number: '',
    notes: '',
  });

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.getById(id).then(res => {
      // DEBUG: Log risposta API - verifica struttura
      console.log('========== DEBUG INVOICE DETAIL ==========');
      console.log('ID richiesto:', id);
      console.log('res.data COMPLETO:', JSON.stringify(res.data, null, 2));
      const inv = res.data?.data || res.data; // potrebbe essere annidato
      console.log('Fattura estratta:');
      console.log('  - id:', inv?.id);
      console.log('  - nuf:', inv?.nuf);
      console.log('  - f_totale:', inv?.f_totale);
      console.log('  - f_paga_imp:', inv?.f_paga_imp);
      console.log('  - paid_amount:', inv?.paid_amount);
      console.log('  - saldo_residuo:', inv?.saldo_residuo);
      console.log('==========================================');
      return res.data;
    })
  });
  useEffect(() => {
    if (params.get('payment') === '1' && invoice) {
      const inv = invoice.data || invoice;
      setPaymentData(current => ({ ...current, amount: Math.abs(Number(inv.f_totale) - Number(inv.f_paga_imp || 0)).toFixed(2) }));
      setPaymentDialogOpen(true);
      const next = new URLSearchParams(params); next.delete('payment'); setParams(next, { replace: true });
    }
  }, [invoice, params, setParams]);

  // Query per lo storico pagamenti unificato (acconti FoxPro + nuovi pagamenti)
  const { data: paymentsHistory } = useQuery({
    queryKey: ['invoicePaymentsHistory', id],
    queryFn: () => invoicesApi.getPaymentsHistory(id).then(res => {
      // DEBUG: Log storico pagamenti
      console.log('========== DEBUG PAYMENTS HISTORY ==========');
      console.log('Fattura ID:', id);
      console.log('Summary:', res.data?.data?.summary);
      console.log('Payments count:', res.data?.data?.payments?.length || 0);
      console.log('============================================');
      return res.data;
    }),
    enabled: !!invoice
  });

  const linkedMovement=params.get('movement');
  const paymentTarget=params.get('section')==='pagamenti' ? (linkedMovement && paymentsHistory?.data?.payments?.some(p=>p.id===linkedMovement) ? 'invoice-movement-'+linkedMovement : 'invoice-payment-history') : null;
  useCrmFocus(paymentTarget,!!paymentsHistory);

  const paymentMutation = useMutation({
    mutationFn: (data) => paymentsApi.create({ invoice_id: id, importo: data.amount,
      data_pagamento: data.payment_date, modalita: data.payment_method, riferimento: data.reference_number, note: data.notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoicePaymentsHistory', id] });
      setPaymentDialogOpen(false);
      setPaymentData({ amount: '', payment_date: new Date(), payment_method: 'cash', reference_number: '', notes: '' });
    },
  });

  // Mutation per modifica acconto
  const updateAdvanceMutation = useMutation({
    mutationFn: ({ advanceId, data }) => advancesApi.update(advanceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoicePaymentsHistory', id] });
      setEditPaymentDialogOpen(false);
      setEditingPayment(null);
    },
  });

  // Mutation per modifica pagamento
  const updatePaymentMutation = useMutation({
    mutationFn: ({ paymentId, data }) => paymentsApi.update(paymentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoicePaymentsHistory', id] });
      setEditPaymentDialogOpen(false);
      setEditingPayment(null);
    },
  });

  // Mutation per eliminazione acconto
  const deleteAdvanceMutation = useMutation({
    mutationFn: (advanceId) => advancesApi.delete(advanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoicePaymentsHistory', id] });
      setDeletePaymentDialogOpen(false);
      setDeletingPayment(null);
    },
  });

  // Mutation per eliminazione pagamento
  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId) => paymentsApi.delete(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoicePaymentsHistory', id] });
      setDeletePaymentDialogOpen(false);
      setDeletingPayment(null);
    },
  });

  const deletePermanentMutation = useMutation({
    mutationFn: () => invoicesApi.deletePermanent(id),
    onSuccess: () => {
      for (const key of ['invoices', 'invoice', 'refillAvailable', 'customerInvoices', 'plantDeliveries']) queryClient.invalidateQueries({ queryKey: [key] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      setDeleteDialogOpen(false);
      navigate('/crm/fatture');
    },
  });

  const cancelCreditMutation = useMutation({ mutationFn: () => invoicesApi.delete(id),
    onSuccess: () => { for (const key of ['invoice', 'invoices', 'creditNoteDraft']) queryClient.invalidateQueries({ queryKey: [key] }); } });
  const cancelCreditNote = () => {
    if (window.confirm('Annullare questa nota di credito? L’importo tornerà disponibile per un nuovo storno.')) cancelCreditMutation.mutate();
  };

  const openDeleteDialog = async () => {
    setDeleteDialogOpen(true);
    setDeletePreview(null);
    setDeletePreviewError(null);
    setDeletePreviewLoading(true);
    try {
      const res = await invoicesApi.getDeletePreview(id);
      setDeletePreview(res.data?.data || null);
    } catch (e) {
      setDeletePreviewError(e?.response?.data?.message || 'Errore nel caricamento anteprima cancellazione');
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  const handleDownloadPdf = (layout = 'standard') => downloadPdf(invoice.data || invoice, { layout, includeYear: true });
  if (isLoading) {
    return (
      <Box>
        <Skeleton height={60} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton height={80} /></Grid>
          <Grid item xs={12} md={6}><Skeleton height={300} /></Grid>
          <Grid item xs={12} md={6}><Skeleton height={300} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/crm/fatture')}>Torna alla lista</Button>
        <Alert severity="error" sx={{ mt: 2 }}>Fattura non trovata</Alert>
      </Box>
    );
  }

  // Estrai dati fattura
  const inv = invoice.data || invoice;
  const credit = isCreditNote(inv);
  const issuedCredit = isIssuedCreditNote(inv);
  const isRefill = inv.billing_type === 'refill';
  const refillLocked = isRefill && (inv.stato === 'annullata' || Number(inv.f_paga_imp) > 0 || inv.payments?.length || inv.advances?.length);
  const customer = inv.customer || inv.Customer;
  
  // Campi header
  const nuf = formatNuf(inv.sezionale, inv.nuf);
  const contatore = inv.f_nu_conta || inv.credit_note_snapshot?.source.meter_number || '-';
  const contratto = inv.f_contratt || '-';
  const codCli = inv.f_cod_cli || '-';
  const stato = inv.stato || 'emessa';
  const prezzoLitro = parseFloat(inv.f_lire_lit) || 0;
  
  // Dati cliente
  const nome = inv.f_nome || formatCustomerName(customer);
  const localita = inv.f_localita || '';
  const citta = inv.f_citta || '';
  const provincia = inv.f_prov || '';
  const partitaIva = inv.f_piva || customer?.partita_iva || '';
  const codiceFiscale = inv.f_cif || customer?.codice_fiscale || '';
  const varieCausale = inv.f_var_caus || '';
  
  // Date
  const dataEmissione = inv.f_emission;
  const dataScadenza = inv.f_ult_gior;
  const dataPagamento = inv.f_paga_dt;
  const dataStampa = inv.f_dt_stamp;
  const periodoRif = inv.f_peri_rif || '';
  
  // Letture contatore (divise per 4.15 per MC)
  const lettAttuale = parseFloat(inv.f_lett_att) || 0;
  const lettPrecedente = parseFloat(inv.f_lett_pre) || 0;
  const mcAttuale = lettAttuale / 4.15;
  const mcPrecedente = lettPrecedente / 4.15;
  const consumoMc = parseFloat(inv.f_cons_mc) || (mcAttuale - mcPrecedente);
  const consumoLit = parseFloat(inv.f_cons_lit) || (lettAttuale - lettPrecedente);
  
  // Date lettura
  const dataLetturaAttuale = inv.f_data_att;
  const dataLetturaPrecedente = inv.f_data_pre;
  const tipoLettura = inv.tipo_lettura || '';
  
  // Dati impianto
  const ubicazione = inv.f_ubicazio || '';
  const interventiImporto = parseFloat(inv.f_interventi) || 0;
  const interventiCausale = inv.f_int_caus || '';
  
  // Fatture insolute (può essere un array di oggetti o una stringa)
  const fattureInsoluteRaw = inv.fattureInsolute;
  const fattureInsolute = credit ? [] : Array.isArray(fattureInsoluteRaw) ? fattureInsoluteRaw :
                          (typeof fattureInsoluteRaw === 'string' && fattureInsoluteRaw ? [{ numero: fattureInsoluteRaw }] : []);
  
  // Importi
  const allaccio = parseFloat(inv.f_allacio) || 0;
  const acconto = parseFloat(inv.f_acconto) || 0;
  const importoGas = parseFloat(inv.f_gas) || 0;
  const totParziale = parseFloat(inv.f_tot_parz) || 0;
  const varie = parseFloat(inv.f_varie) || 0;
  const quotaMese = parseFloat(inv.f_quota_me) || 0;
  const tasse = parseFloat(inv.f_tasse) || 0;
  const mora = parseFloat(inv.f_mora) || 0;
  const spedizione = parseFloat(inv.f_sped_bol) || 0;
  const arrotondamento = parseFloat(inv.f_arrotond) || 0;
  const totale = parseFloat(inv.f_totale) || 0;
  const pagato = parseFloat(inv.f_paga_imp) || 0;
  const saldoResiduo = totale - pagato;
  
  const statusConfig = invoiceState(inv);
  const isOverdue = !credit && dataScadenza && differenceInDays(new Date(), new Date(dataScadenza)) > 0 && stato !== 'pagata' && stato !== 'annullata';
  const giorniRitardo = isOverdue ? differenceInDays(new Date(), new Date(dataScadenza)) : 0;

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      {/* Header con azioni */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/crm/fatture')} size={isMobile ? 'small' : 'medium'}><ArrowBackIcon /></IconButton>
          <Typography variant={isMobile ? 'subtitle1' : 'h5'} fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} noWrap>
            <ReceiptIcon color="primary" fontSize={isMobile ? 'small' : 'medium'} /> {credit ? 'NOTA DI CREDITO' : 'FATTURA'}
          </Typography>
        </Box>
        {isMobile ? (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button disabled={!!refillLocked || issuedCredit} variant="contained" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/crm/fatture/${id}/modifica`)}>Modifica</Button>
            <IconButton onClick={(e) => setMobileActionsAnchor(e.currentTarget)}><MoreVertIcon /></IconButton>
            <Menu anchorEl={mobileActionsAnchor} open={Boolean(mobileActionsAnchor)} onClose={() => setMobileActionsAnchor(null)}>
              {canCreateCreditNote(inv) && <MenuItem onClick={() => { setMobileActionsAnchor(null); navigate(`/crm/fatture/${id}/nota-credito`); }}>Crea nota di credito</MenuItem>}
              {issuedCredit && stato !== 'annullata' && <MenuItem disabled={cancelCreditMutation.isPending} onClick={() => { setMobileActionsAnchor(null); cancelCreditNote(); }}>Annulla nota di credito</MenuItem>}
              <MenuItem disabled={!!pdfDownloading} onClick={() => { setMobileActionsAnchor(null); handleDownloadPdf(); }}>
                <PdfIcon sx={{ mr: 1 }} fontSize="small" /> PDF standard
              </MenuItem>
              {!credit && <MenuItem disabled={!!pdfDownloading} onClick={() => { setMobileActionsAnchor(null); handleDownloadPdf('payments'); }}><PdfIcon sx={{ mr: 1 }} fontSize="small" /> PDF con pagamenti</MenuItem>}
              <MenuItem onClick={() => { setMobileActionsAnchor(null); setSendEmailDialogOpen(true); }}>
                <SendIcon sx={{ mr: 1 }} fontSize="small" /> Invia Email
              </MenuItem>
              <MenuItem onClick={() => { setMobileActionsAnchor(null); setSendWhatsappDialogOpen(true); }}>
                <WhatsAppIcon sx={{ mr: 1, color: '#25D366' }} fontSize="small" /> Invia WhatsApp
              </MenuItem>
              <MenuItem onClick={() => { setMobileActionsAnchor(null); setPaymentData(prev => ({ ...prev, amount: Math.abs(saldoResiduo).toFixed(2) })); setPaymentDialogOpen(true); }}>
                <PaymentIcon sx={{ mr: 1 }} fontSize="small" /> {credit ? 'Registra rimborso' : 'Registra Pagamento'}
              </MenuItem>
              <MenuItem onClick={() => { setMobileActionsAnchor(null); openDeleteDialog(); }}>
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" /> Elimina
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button disabled={!!pdfDownloading} variant="outlined" size="small" startIcon={<PdfIcon />} onClick={() => handleDownloadPdf()}>PDF standard</Button>
            {!credit && <Button disabled={!!pdfDownloading} variant="outlined" size="small" startIcon={<PdfIcon />} onClick={() => handleDownloadPdf('payments')}>PDF con pagamenti</Button>}
            <Button variant="outlined" size="small" startIcon={<SendIcon />} onClick={() => setSendEmailDialogOpen(true)}>Invia Email</Button>
            <Button variant="outlined" size="small" startIcon={<WhatsAppIcon />} sx={{ color: '#25D366', borderColor: '#25D366', '&:hover': { borderColor: '#128C7E', bgcolor: '#25D36610' } }} onClick={() => setSendWhatsappDialogOpen(true)}>WhatsApp</Button>
            <Button variant="outlined" size="small" startIcon={<PaymentIcon />} onClick={() => {
              setPaymentData(prev => ({ ...prev, amount: Math.abs(saldoResiduo).toFixed(2) }));
              setPaymentDialogOpen(true);
            }}>{credit ? 'Registra rimborso' : 'Registra Pagamento'}</Button>
            {canCreateCreditNote(inv) && <Button variant="outlined" size="small" onClick={() => navigate(`/crm/fatture/${id}/nota-credito`)}>Crea nota di credito</Button>}
            {issuedCredit && stato !== 'annullata' && <Button variant="outlined" size="small" disabled={cancelCreditMutation.isPending} onClick={cancelCreditNote}>Annulla nota</Button>}
            <Button disabled={!!refillLocked || issuedCredit} variant="contained" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/crm/fatture/${id}/modifica`)}>Modifica</Button>
            <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={openDeleteDialog}>Elimina</Button>
          </Box>
        )}
      </Box>

      {pdfDownloading && <Alert severity="info" sx={{ mb: 2 }}>Preparazione del PDF{pdfDownloading === 'payments' ? ' con pagamenti' : ' standard'}…</Alert>}
      {pdfError && <Alert severity="error" onClose={clearPdfError} sx={{ mb: 2 }}>{pdfError}</Alert>}
      {cancelCreditMutation.error && <Alert severity="error" sx={{ mb: 2 }}>{cancelCreditMutation.error.response?.data?.message || 'Errore nell’annullamento della nota'}</Alert>}
      {issuedCredit && <Alert severity="info" sx={{ mb: 2 }}>Riferita alla fattura {inv.credit_note_snapshot.source.number} del {formatDate(inv.credit_note_snapshot.source.issue_date)}.
        <Button size="small" onClick={() => navigate('/crm/fatture/' + inv.credit_note_for_invoice_id)}>Apri fattura originale</Button>
      </Alert>}
      {!!inv.credit_notes?.length && <Card sx={{ mb: 2 }}><CardContent><Typography variant="subtitle1" fontWeight="bold">Note di credito collegate</Typography>
        {inv.credit_notes.map(note => <Box key={note.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}><Button onClick={() => navigate('/crm/fatture/' + note.id)}>{formatNuf(note.sezionale, note.nuf)}</Button><Typography>{formatCurrency(note.f_totale)}</Typography>{note.stato === 'annullata' && <Chip size="small" label="Annullata"/>}</Box>)}
        <Typography variant="body2">Ancora stornabile: {formatCurrency(inv.remaining_amount)}</Typography>
      </CardContent></Card>}

      {isRefill && <Chip label="Da rifornimenti" color="primary" sx={{ mb: 2 }}/>}
      {/* Header dati principali stile FoxPro */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2, bgcolor: '#f5f5f5' }}>
        <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
          <Grid item xs={12} md={9}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 2 } }}>
              <InlineField label="Nuf" value={<strong>{nuf}</strong>} />
              {!isRefill && <InlineField label="Contatore" value={contatore} />}
              <InlineField label="Contratto" value={contratto} />
              <InlineField label="Cod_cli" value={codCli} />
              {!isRefill && <InlineField label="€/lit" value={prezzoLitro.toFixed(2)} />}
              <InlineField label="IVA" value={`${inv.f_al_iva ?? 22}%`} />
            </Box>
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
            <Chip label={statusConfig.label} color={statusConfig.color} />
            {isOverdue && <Chip label={`SCADUTA (${giorniRitardo}gg)`} color="error" size="small" sx={{ ml: 1 }} />}
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {/* Colonna sinistra */}
        <Grid item xs={12} md={6}>
          {/* Alert Fatture Insolute */}
          {fattureInsolute.length > 0 && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">FATTURE INSOLUTE ({fattureInsolute.length})</Typography>
              {fattureInsolute.map((fatt, idx) => (
                <Typography key={idx} variant="body2">
                  • Fatt. n° {fatt.numero || fatt.id} del {formatDate(fatt.emissione)} - {formatCurrency(fatt.importo)} 
                  {fatt.mora > 0 && ` (mora: ${formatCurrency(fatt.mora)})`}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Dati Cliente */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> DATI CLIENTE
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <FieldRow label="Nome / Ragione Sociale" value={nome} bold />
              <FieldRow label="Località" value={localita || '-'} />
              <FieldRow label="Città" value={`${citta} ${provincia ? `(${provincia})` : ''}`} />
              <FieldRow label="P.IVA" value={partitaIva || '-'} />
              <FieldRow label="Codice Fiscale" value={codiceFiscale || '-'} />
              <Box sx={{ mt: 1 }}>
                <Button size="small" startIcon={<PersonIcon />} onClick={() => navigate(`/crm/clienti/${inv.customer_id}`)} variant="outlined">
                  Vai al Cliente
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Dati Impianto */}
          <Card sx={{ mt: 2, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'info.dark' }}>
                <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> DATI IMPIANTO
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <FieldRow label="Ubicazione" value={ubicazione || '-'} bold />
              {!isRefill && <FieldRow label="Matr. Contatore" value={contatore} />}
              {!isRefill && <FieldRow label="Tipo Lettura" value={tipoLettura || 'Non specificato'} />}
              {(interventiImporto !== 0 || interventiCausale) && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Intervento extra</Typography>
                  <Typography variant="body2">{interventiCausale || 'Causale non specificata'}</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatCurrency(interventiImporto)}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Date */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> DATE DOCUMENTO
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <FieldRow label="Data Emissione" value={formatDate(dataEmissione)} bold />
                  <FieldRow label="Periodo Rif." value={periodoRif || '-'} />
                  <FieldRow label="Data Stampa" value={formatDate(dataStampa)} />
                </Grid>
                <Grid item xs={6}>
                  <FieldRow label="Scadenza" value={formatDate(dataScadenza)} valueColor={isOverdue ? 'error.main' : 'inherit'} bold />
                  <FieldRow label="Data Pagamento" value={formatDate(dataPagamento)} valueColor={dataPagamento ? 'success.main' : 'inherit'} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Colonna destra */}
        <Grid item xs={12} md={6}>
          {issuedCredit ? <Card><CardContent><CreditNoteSummary invoice={inv}/></CardContent></Card> : isRefill ? <Card><CardContent>
            <Typography variant="h6" gutterBottom>Rifornimenti fatturati</Typography>
            <RefillRows items={inv.refill_items || []}/>
          </CardContent></Card> : <>
          {/* Letture Contatore */}
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'warning.dark' }}>
                <SpeedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> LETTURE CONTATORE
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">LETTURA ATTUALE</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">{formatNumber(mcAttuale, 0)} MC</Typography>
                    <Typography variant="caption" color="text.secondary">({formatNumber(mcAttuale * 4.15, 2)} litri)</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      📅 {formatDate(dataLetturaAttuale) || 'Data non disponibile'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">LETTURA PRECEDENTE</Typography>
                    <Typography variant="h6" fontWeight="bold">{formatNumber(mcPrecedente, 0)} MC</Typography>
                    <Typography variant="caption" color="text.secondary">({formatNumber(mcPrecedente * 4.15, 2)} litri)</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      📅 {formatDate(dataLetturaPrecedente) || 'Data non disponibile'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: 'warning.main', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="white">CONSUMO PERIODO</Typography>
                    <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" color="white">{formatNumber(consumoMc, 0)} MC</Typography>
                    <Typography variant="body2" color="white">({formatNumber(consumoLit, 2)} litri)</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          </>}
          {!issuedCredit && (isRefill ? <Card sx={{ mt: 2 }}><CardContent><RefillInvoiceSummary invoice={inv}/></CardContent></Card> : <>
          {/* Importi */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>💰 IMPORTI</Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>IMPONIBILE</Typography>
                  <FieldRow label="Importo Gas" value={formatCurrency(importoGas)} bold />
                  <FieldRow label="Quota Mese" value={formatCurrency(quotaMese)} />
                  <FieldRow label={interventiCausale ? `Interventi (${interventiCausale})` : 'Interventi'} value={formatCurrency(interventiImporto)} />
                  <FieldRow label="Allaccio" value={formatCurrency(allaccio)} />
                  <FieldRow label={varieCausale ? `Varie (${varieCausale})` : "Varie"} value={formatCurrency(varie)} />
                  <FieldRow label="Tot. Parziale" value={formatCurrency(totParziale)} bold />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>ALTRI IMPORTI</Typography>
                  <FieldRow label="Tasse/IVA 22%" value={formatCurrency(tasse)} />
                  <FieldRow label="Acconto" value={formatCurrency(acconto)} valueColor={acconto > 0 ? 'info.main' : 'inherit'} />
                  <FieldRow label="Mora" value={formatCurrency(mora)} valueColor={mora > 0 ? 'error.main' : 'inherit'} />
                  <FieldRow label="Spedizione" value={formatCurrency(spedizione)} />
                  <FieldRow label="Arrotondamento" value={formatCurrency(arrotondamento)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          </>)}
          {/* Totale e Saldo */}
          <Card sx={{ mt: 2, bgcolor: saldoResiduo > 0 ? '#ffebee' : '#e8f5e9' }}>
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5 }, bgcolor: 'primary.main', borderRadius: 1 }}>
                    <Typography variant="caption" color="white">{credit ? 'TOTALE NOTA DI CREDITO' : 'TOTALE FATTURA'}</Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="white">{formatCurrency(totale)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5 }, bgcolor: pagato > 0 ? 'success.main' : 'grey.400', borderRadius: 1 }}>
                    <Typography variant="caption" color="white">{credit ? 'GIÀ REGOLATO' : 'GIÀ PAGATO'}</Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="white">{formatCurrency(pagato)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 }, mt: 1, bgcolor: saldoResiduo > 0 ? 'error.main' : 'success.main', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="white">{credit ? 'CREDITO RESIDUO' : 'SALDO DA PAGARE'}</Typography>
                    <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight="bold" color="white">{formatCurrency(saldoResiduo)}</Typography>
                    {statusConfig.status === 'pagata' && <Chip label={credit ? '✓ REGOLATO' : '✓ SALDATO'} size="small" sx={{ mt: 1, bgcolor: 'white', color: 'success.main' }} />}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {!credit && stato !== 'annullata' && saldoResiduo > 0 && <Box sx={{ mt: 2 }}><PayPalInvoiceLink invoiceId={id} balance={saldoResiduo} /></Box>}
      {!credit && <PayPalPaymentAudit invoiceId={id} />}
      {/* Storico Pagamenti Unificato (Acconti FoxPro + Nuovi Pagamenti) */}
      <Card id="invoice-payment-history" tabIndex={-1} sx={{ mt: 2, scrollMarginTop: '90px' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Storico Pagamenti
              {paymentsHistory?.data?.summary && (
                <Chip 
                  size="small" 
                  label={`${(paymentsHistory.data.summary.count_advances || 0) + (paymentsHistory.data.summary.count_payments || 0)} movimenti`}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => {
              setPaymentData(prev => ({ ...prev, amount: Math.abs(saldoResiduo).toFixed(2) }));
              setPaymentDialogOpen(true);
            }}>
              Nuovo Pagamento
            </Button>
          </Box>
          {isMobile ? (
            /* Layout mobile: card per ogni pagamento */
            <Box>
              {!paymentsHistory?.data?.payments || paymentsHistory.data.payments.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Nessun pagamento registrato</Typography>
              ) : (
                paymentsHistory.data.payments.map((payment) => (
                  <Paper key={`${payment.type}-${payment.id}`} id={'invoice-movement-'+payment.id} tabIndex={-1} variant="outlined" sx={{ p: 1.5, mb: 1, scrollMarginTop:'90px', ...(linkedMovement===payment.id?{outline:'2px solid',outlineColor:'primary.main',bgcolor:'action.selected'}:{}) }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold">{formatDate(payment.date)}</Typography>
                          <Chip 
                            label={payment.source === 'foxpro' ? 'FoxPro' : 'Nuovo'}
                            size="small"
                            color={payment.source === 'foxpro' ? 'default' : 'success'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        {payment.payment_method && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.payment_method === 'cash' ? 'Contanti' : 
                             payment.payment_method === 'bank_transfer' ? 'Bonifico' :
                             payment.payment_method === 'bank_draft' ? 'Assegno' :
                             payment.paypal?.environment === 'sandbox' ? 'Sandbox - prova: ' + (payment.payment_method === 'paypal_unassign' ? 'Annullamento associazione' : payment.payment_method === 'paypal_refund' ? 'Rimborso' : 'PayPal') : payment.payment_method === 'rid' ? 'RID' : payment.payment_method === 'paypal' ? 'PayPal' : payment.payment_method === 'paypal_refund' ? 'Rimborso / storno PayPal' : payment.payment_method === 'paypal_unassign' ? 'Annullamento associazione PayPal' : payment.payment_method}
                          </Typography>
                        )}
                        {payment.notes && <Typography variant="caption" color="text.secondary" sx={{ ...paymentNoteSx, display: 'block', mt: 0.25 }}>{payment.notes}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body1" fontWeight="bold" color="success.main">{formatCurrency(payment.amount)}</Typography>
                        <IconButton disabled={String(payment.payment_method).startsWith('paypal')} size="small" color="primary" onClick={() => { setEditingPayment({ ...payment, amount: Math.abs(payment.amount) }); setEditPaymentDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton disabled={String(payment.payment_method).startsWith('paypal')} size="small" color="error" onClick={() => { setDeletingPayment(payment); setDeletePaymentDialogOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Data</TableCell>
                  <TableCell>Tipo/Causale</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Origine</TableCell>
                  <TableCell align="right">Importo</TableCell>
                  <TableCell align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!paymentsHistory?.data?.payments || paymentsHistory.data.payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>Nessun pagamento registrato</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paymentsHistory.data.payments.map((payment) => (
                    <TableRow key={`${payment.type}-${payment.id}`} id={'invoice-movement-'+payment.id} tabIndex={-1} selected={linkedMovement===payment.id} sx={{scrollMarginTop:'90px'}} hover>
                      <TableCell>{formatDate(payment.date)}</TableCell>
                      <TableCell>
                        {payment.payment_method ? (
                          <Chip 
                            label={payment.payment_method === 'cash' ? 'Contanti' : 
                                   payment.payment_method === 'bank_transfer' ? 'Bonifico' :
                                   payment.payment_method === 'bank_draft' ? 'Assegno' :
                                   payment.paypal?.environment === 'sandbox' ? 'Sandbox - prova: ' + (payment.payment_method === 'paypal_unassign' ? 'Annullamento associazione' : payment.payment_method === 'paypal_refund' ? 'Rimborso' : 'PayPal') : payment.payment_method === 'rid' ? 'RID' : payment.payment_method === 'paypal' ? 'PayPal' : payment.payment_method === 'paypal_refund' ? 'Rimborso / storno PayPal' : payment.payment_method === 'paypal_unassign' ? 'Annullamento associazione PayPal' : payment.payment_method}
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ ...paymentNoteSx, minWidth: 180, maxWidth: 420 }}>
                          {payment.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={payment.source === 'foxpro' ? 'FoxPro' : 'Nuovo'}
                          size="small"
                          color={payment.source === 'foxpro' ? 'default' : 'success'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => {
                            setEditingPayment({ ...payment, amount: Math.abs(payment.amount) });
                            setEditPaymentDialogOpen(true);
                          }}
                          title="Modifica"
                          disabled={String(payment.payment_method).startsWith('paypal')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            setDeletingPayment(payment);
                            setDeletePaymentDialogOpen(true);
                          }}
                          title="Elimina"
                          disabled={String(payment.payment_method).startsWith('paypal')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          {/* Riepilogo totali */}
          {paymentsHistory?.data?.summary && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Totale Fattura</Typography>
                  <Typography variant="body1" fontWeight="bold">{formatCurrency(paymentsHistory.data.summary.totale_fattura)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Totale Pagato</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">{formatCurrency(paymentsHistory.data.summary.totale_pagato)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Saldo Residuo</Typography>
                  <Typography variant="body1" fontWeight="bold" color={paymentsHistory.data.summary.saldo_residuo > 0 ? 'error.main' : 'success.main'}>
                    {formatCurrency(paymentsHistory.data.summary.saldo_residuo)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog cancellazione definitiva */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletePermanentMutation.isPending && setDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {deletePreview?.warnings?.isCreditNote ? 'Elimina nota di credito definitivamente' : deletePreview?.invoice?.billing_type === 'refill' ? 'Elimina fattura da rifornimenti definitivamente' : 'Elimina fattura definitivamente'}
        </DialogTitle>
        <DialogContent dividers>
          {deletePreviewLoading && (
            <Box>
              <Skeleton height={28} />
              <Skeleton height={28} />
              <Skeleton height={200} />
            </Box>
          )}

          {!deletePreviewLoading && deletePreviewError && (
            <Alert severity="error">{deletePreviewError}</Alert>
          )}

          {!deletePreviewLoading && deletePreview && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {deletePreview.warnings?.isLinkedCreditNote ? 'La nota di credito verrà eliminata. Il relativo importo tornerà disponibile per uno storno della fattura originale.' : deletePreview.invoice?.billing_type === 'refill' ? 'La fattura e le sue righe verranno eliminate definitivamente. I rifornimenti originali saranno conservati e torneranno disponibili per una nuova fattura.' : 'Stai per eliminare definitivamente il documento e tutte le letture contatore collegate.'}
              </Typography>
              {deletePreview.warnings?.hasCreditNotes && <Alert severity="error" sx={{ mb: 2 }}>Elimina prima le note di credito collegate alla fattura.</Alert>}

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <InlineField label="NUF" value={<strong>{formatNuf(deletePreview.invoice?.sezionale, deletePreview.invoice?.nuf) ?? '-'}</strong>} />
                  <InlineField label="Data" value={formatDate(deletePreview.invoice?.f_emission)} />
                  <InlineField
                    label="Cliente"
                    value={deletePreview.invoice?.f_nome || formatCustomerName(deletePreview.invoice?.customer)}
                  />
                  <InlineField label="Totale" value={formatCurrency(deletePreview.invoice?.f_totale)} />
                </Box>
              </Paper>

              {(deletePreview.warnings?.hasPayments || deletePreview.warnings?.hasAdvances) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {deletePreview.invoice?.billing_type === 'refill' ? 'Sono presenti pagamenti o acconti: rimuovili prima di eliminare definitivamente la fattura.' : 'Sono presenti pagamenti/acconti collegati: verranno eliminati insieme alla fattura.'}
                  {' '}
                  {deletePreview.totals?.payments_count
                    ? `(Pagamenti: ${deletePreview.totals.payments_count}, Totale: ${formatCurrency(deletePreview.totals.payments_total)})`
                    : ''}
                  {' '}
                  {deletePreview.totals?.advances_count
                    ? `(Acconti: ${deletePreview.totals.advances_count}, Totale: ${formatCurrency(deletePreview.totals.advances_total)})`
                    : ''}
                </Alert>
              )}

              {deletePreview.warnings?.hasInstallmentPlans && <Alert severity="warning" sx={{ mb: 2 }}>La fattura è collegata a una rateizzazione: scollegala dal piano prima di eliminarla.</Alert>}
              {deletePreview.warnings?.hasExtraInterventionsLinked && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Interventi extra collegati: verranno riportati in sospeso.
                  {deletePreview.totals?.interventions_count
                    ? ` (Interventi: ${deletePreview.totals.interventions_count}, Totale: ${formatCurrency(deletePreview.totals.interventions_total)})`
                    : ''}
                </Alert>
              )}

              {deletePreview.invoice?.billing_type === 'refill' ? <RefillRows items={deletePreview.refill_items || []}/> : <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Letture collegate ({deletePreview.meterReadings?.length || 0})
              </Typography>

              {(deletePreview.meterReadings?.length || 0) === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nessuna lettura collegata.
                </Typography>
              ) : (
                isMobile ? (
                  <MobileCardList
                    rows={(deletePreview.meterReadings || []).map((reading) => ({
                      ...reading,
                      meter_display: reading.meter_number || reading.meter_id || '-',
                    }))}
                    primaryField="meter_display"
                    secondaryField="data_lettura"
                    extraFields={[
                      {
                        field: 'data_lettura',
                        label: 'Data',
                        render: (row) => formatDate(row.data_lettura),
                      },
                      {
                        field: 'consumo_litri',
                        label: 'Consumo',
                        render: (row) => `${formatNumber(row.consumo_litri, 0)} L`,
                      },
                      {
                        field: 'importo_totale',
                        label: 'Importo',
                        render: (row) => formatCurrency(row.importo_totale),
                      },
                    ]}
                  />
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Contatore</TableCell>
                          <TableCell>Data lettura</TableCell>
                          <TableCell align="right">Consumo (L)</TableCell>
                          <TableCell align="right">Importo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(deletePreview.meterReadings || []).map((r) => (
                          <TableRow key={r.id}>
                            <TableCell>{r.meter_number || r.meter_id || '-'}</TableCell>
                            <TableCell>{formatDate(r.data_lettura)}</TableCell>
                            <TableCell align="right">{formatNumber(r.consumo_litri, 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(r.importo_totale)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              )}

              </>}
              <Alert severity="warning" sx={{ mt: 2 }}>
                {deletePreview.invoice?.billing_type === 'refill' ? 'Confermando, la fattura sarà rimossa definitivamente, anche se già annullata. I rifornimenti potranno essere fatturati nuovamente. Il progressivo non viene riutilizzato automaticamente.' : 'Confermando, la fattura verrrà eliminata definitivamente. Il progressivo (NUF) non verrà riutilizzato.'}
              </Alert>

              {deletePermanentMutation.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {deletePermanentMutation.error?.response?.data?.message || 'Errore durante la cancellazione'}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletePermanentMutation.isPending}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deletePermanentMutation.mutate()}
            disabled={deletePreviewLoading || !!deletePreviewError || !deletePreview || deletePermanentMutation.isPending || deletePreview?.warnings?.hasCreditNotes || ((deletePreview?.invoice?.billing_type === 'refill' || deletePreview?.warnings?.isLinkedCreditNote) && (deletePreview.warnings?.hasPayments || deletePreview.warnings?.hasAdvances || deletePreview.warnings?.hasInstallmentPlans))}
          >
            Elimina definitivamente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Pagamento */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{credit ? 'Registra rimborso / compensazione' : 'Registra Pagamento'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Importo"
                type="number"
                fullWidth
                value={paymentData.amount}
                onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                InputProps={{ startAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data Pagamento"
                value={paymentData.payment_date}
                onChange={(date) => setPaymentData(prev => ({ ...prev, payment_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Metodo</InputLabel>
                <Select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                  label="Metodo"
                >
                  <MenuItem value="cash">Contanti</MenuItem>
                  <MenuItem value="bank_transfer">Bonifico</MenuItem>
                  <MenuItem value="bank_draft">Assegno</MenuItem>
                  <MenuItem value="rid">RID</MenuItem>
                  <MenuItem value="credit_card">Carta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Riferimento"
                fullWidth
                value={paymentData.reference_number}
                onChange={(e) => setPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Note"
                fullWidth
                multiline
                rows={2}
                value={paymentData.notes}
                onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={() => paymentMutation.mutate({
              ...paymentData,
              amount: parseFloat(paymentData.amount),
              payment_date: paymentData.payment_date?.toISOString().split('T')[0],
            })}
            disabled={Number(paymentData.amount) <= 0 || !paymentData.amount || paymentMutation.isPending}
          >
            Registra
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog modifica pagamento/acconto */}
      <Dialog open={editPaymentDialogOpen} onClose={() => setEditPaymentDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Modifica {editingPayment?.type === 'advance' ? 'Acconto' : 'Pagamento'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data"
                value={editingPayment ? new Date(editingPayment.date) : new Date()}
                onChange={(newDate) => setEditingPayment(prev => ({ ...prev, date: newDate?.toISOString().split('T')[0] }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Importo"
                type="number"
                fullWidth
                value={editingPayment?.amount || ''}
                onChange={(e) => setEditingPayment(prev => ({ ...prev, amount: e.target.value }))}
                inputProps={{ step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Metodo Pagamento</InputLabel>
                <Select
                  value={editingPayment?.payment_method || ''}
                  label="Metodo Pagamento"
                  onChange={(e) => setEditingPayment(prev => ({ ...prev, payment_method: e.target.value }))}
                >
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value="cash">Contanti</MenuItem>
                  <MenuItem value="bank_transfer">Bonifico</MenuItem>
                  <MenuItem value="bank_draft">Assegno</MenuItem>
                  <MenuItem value="rid">RID</MenuItem>
                  <MenuItem value="BONIFICO">BONIFICO</MenuItem>
                  <MenuItem value="CONTANTI">CONTANTI</MenuItem>
                  <MenuItem value="BOLLETTINO">BOLLETTINO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Note"
                fullWidth
                multiline
                rows={2}
                value={editingPayment?.notes || ''}
                onChange={(e) => setEditingPayment(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
            {/* Campo per modificare direttamente il totale pagato della fattura */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
                ⚠️ Modifica manuale totale pagato fattura (opzionale)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Totale Pagato Fattura (f_paga_imp)"
                type="number"
                fullWidth
                value={editingPayment?.invoice_paid_amount ?? ''}
                onChange={(e) => setEditingPayment(prev => ({ ...prev, invoice_paid_amount: e.target.value }))}
                inputProps={{ step: '0.01' }}
                helperText="Lascia vuoto per calcolo automatico"
                color="warning"
                focused={editingPayment?.invoice_paid_amount !== undefined && editingPayment?.invoice_paid_amount !== ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Totale Fattura (riferimento)"
                type="number"
                fullWidth
                value={paymentsHistory?.data?.summary?.totale_fattura?.toFixed(2) || ''}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditPaymentDialogOpen(false); setEditingPayment(null); }}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              // Estrai ID numerico dal prefisso (adv_123 -> 123, pay_456 -> 456)
              const numericId = String(editingPayment.id).replace(/^(adv_|pay_)/, '');
              
              if (editingPayment?.type === 'advance') {
                updateAdvanceMutation.mutate({
                  advanceId: numericId,
                  data: {
                    ac_paga_dt: editingPayment.date,
                    ac_paga_imp: parseFloat(editingPayment.amount),
                    note: editingPayment.notes,
                    invoice_paid_amount: editingPayment.invoice_paid_amount ? parseFloat(editingPayment.invoice_paid_amount) : null
                  }
                });
              } else {
                updatePaymentMutation.mutate({
                  paymentId: numericId,
                  data: {
                    data_pagamento: editingPayment.date,
                    importo: Math.abs(parseFloat(editingPayment.amount)),
                    modalita: editingPayment.payment_method,
                    note: editingPayment.notes
                  }
                });
              }
            }}
            disabled={updateAdvanceMutation.isPending || updatePaymentMutation.isPending}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione pagamento/acconto */}
      <Dialog open={deletePaymentDialogOpen} onClose={() => setDeletePaymentDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ color: 'error.main' }}>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Conferma Eliminazione
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare questo {deletingPayment?.type === 'advance' ? 'acconto' : 'pagamento'}?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Data:</strong> {formatDate(deletingPayment?.date)}</Typography>
            <Typography variant="body2"><strong>Importo:</strong> {formatCurrency(deletingPayment?.amount)}</Typography>
            <Typography variant="body2"><strong>Note:</strong> {deletingPayment?.notes || '-'}</Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            L'importo pagato della fattura verrà aggiornato automaticamente.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeletePaymentDialogOpen(false); setDeletingPayment(null); }}>Annulla</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => {
              // Estrai ID numerico dal prefisso (adv_123 -> 123, pay_456 -> 456)
              const numericId = String(deletingPayment.id).replace(/^(adv_|pay_)/, '');
              
              if (deletingPayment?.type === 'advance') {
                deleteAdvanceMutation.mutate(numericId);
              } else {
                deletePaymentMutation.mutate(numericId);
              }
            }}
            disabled={deleteAdvanceMutation.isPending || deletePaymentMutation.isPending}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog invio email */}
      <SendEmailDialog
        open={sendEmailDialogOpen}
        onClose={() => setSendEmailDialogOpen(false)}
        invoice={inv}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['invoice', id] });
        }}
      />

      <SendWhatsappDialog
        open={sendWhatsappDialogOpen}
        onClose={() => setSendWhatsappDialogOpen(false)}
        invoice={inv}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['invoice', id] });
          alert('Fattura accodata per invio WhatsApp!');
        }}
      />
    </Box>
  );
};

export default InvoiceDetail;
